import { useRef, useCallback } from 'react';
import * as pc from 'playcanvas';
import { setupGallery, setupCamera, setupLight, setupCar, setupShadowCaster, setupHotspots } from '../../Components/threeD/setup/setupScene';
import { loadAssets } from '../../Components/threeD/setup/loadAssets';
import { setupSimpleOrbitCamera } from './setupSimpleOrbitCamera';
import { setupGrid } from './setupGrid';

/**
 * Custom hook to handle PlayCanvas engine initialization and scene setup
 */
const usePlayCanvasEngine = () => {
    const appRef = useRef(null);
    const cameraEntityRef = useRef(null);
    const carRef = useRef(null);
    const lightsRef = useRef(null);
    const cameraControlsRef = useRef(null);
    const galleryEntityRef = useRef(null);
    const shadowCasterRef = useRef(null);

    const cleanup = useCallback(() => {
        if (appRef.current) {
            // Destroy all entities in the scene
            appRef.current.root.children.slice().forEach(child => {
                child.destroy();
            });

            // Destroy all loaded assets
            appRef.current.assets.list().forEach(asset => {
                asset.unload();
                appRef.current.assets.remove(asset);
            });

            // Destroy the app instance
            appRef.current.destroy();
            appRef.current = null;
        }

        // Clear other refs
        cameraEntityRef.current = null;
        carRef.current = null;
        cameraControlsRef.current = null;
        galleryEntityRef.current = null;
        shadowCasterRef.current = null;
        lightsRef.current = null;
    }, []);

    const initPlayCanvas = useCallback(async (params = {}) => {
        const {
            canvas,
            sku_id,
            bgUrl,
            setProgress,
            galleryFile,
            carFile,
            modelConfig,
            status,
            original = false,
            testingConfig,
            isMobile = false,
            isMobilePortrait = false,
            adminView = false,
            fullControl = false,
            hotspots = false,
            grids = false,
            galleryEnabled = true
        } = params;

        if (!canvas || !modelConfig) {
            console.warn('Missing required parameters for PlayCanvas initialization:', { canvas: !!canvas, modelConfig: !!modelConfig });
            return;
        }

        try {
            // Graphics device setup
            const gfxOptions = {
                deviceTypes: ['webgl2'],
                glslangUrl: '/lib/glslang/glslang.js',
                twgslUrl: '/lib/twgsl/twgsl.js',
                antialias: false,
                powerPreference: 'low-power',
                alpha: false,
                depth: true,
                preserveDrawingBuffer: false,
                maxPixelRatio: isMobile ? 1 : 2
            };

            const device = await pc.createGraphicsDevice(canvas, gfxOptions);
            device.maxPixelRatio = isMobile ? 1.5 : Math.min(window.devicePixelRatio, 2);

            // Create PlayCanvas App options
            const createOptions = new pc.AppOptions();
            createOptions.graphicsDevice = device;
            createOptions.mouse = new pc.Mouse(canvas);
            createOptions.touch = new pc.TouchDevice(canvas);
            createOptions.componentSystems = [
                pc.RenderComponentSystem,
                pc.CameraComponentSystem,
                pc.LightComponentSystem,
                pc.ScriptComponentSystem,
                pc.GSplatComponentSystem,
            ];
            createOptions.resourceHandlers = [pc.TextureHandler, pc.ContainerHandler, pc.ScriptHandler, pc.GSplatHandler];

            // Create the main PlayCanvas app instance
            const app = new pc.AppBase(canvas);
            app.init(createOptions);
            appRef.current = app;

            // Handle canvas fill mode and resolution
            app.setCanvasFillMode(pc.FILLMODE_NONE);
            app.setCanvasResolution(pc.RESOLUTION_AUTO);

            // Resize event listener
            const resize = () => app.resizeCanvas();
            window.addEventListener('resize', resize);

            // Cleanup on component unmount
            app.on('destroy', () => {
                window.removeEventListener('resize', resize);
            });

            // Load assets
            const assets = await loadAssets(
                app, 
                sku_id, 
                bgUrl, 
                setProgress, 
                galleryFile, 
                carFile, 
                modelConfig, 
                status, 
                original, 
                testingConfig
            );

            // Start the PlayCanvas app after assets are loaded
            app.start();

            // Setup scene entities
            if (!original) {
                // Gallery setup
                const galleryEntity = setupGallery(app, assets, sku_id, galleryEnabled, modelConfig);
                galleryEntityRef.current = galleryEntity;

                // Shadow caster setup
                const shadowCaster = setupShadowCaster(app, assets, sku_id, modelConfig);
                shadowCasterRef.current = shadowCaster;
            }

            // Car setup
            setupCar(app, assets, sku_id, carRef, modelConfig, original);

            // Light setup
            const lights = setupLight(app, adminView);
            lightsRef.current = lights;

            // Camera setup
            const camera = setupCamera(app, sku_id, isMobilePortrait, original, modelConfig);
            cameraEntityRef.current = camera;
            cameraControlsRef.current = setupSimpleOrbitCamera(app, camera, carRef.current, modelConfig, fullControl);

            // Optional features
            if (hotspots) {
                setupHotspots(appRef, modelConfig);
            }

            if (grids) {
                const grid = setupGrid(app, modelConfig.scale_factor * 20);
                app.root.addChild(grid);
            }

            return {
                app,
                camera,
                car: carRef.current,
                lights,
                cameraControls: cameraControlsRef.current,
                galleryEntity: galleryEntityRef.current,
                shadowCaster: shadowCasterRef.current
            };

        } catch (error) {
            console.error('Failed to initialize PlayCanvas:', error);
            throw error;
        }
    }, []);

    return {
        // Refs
        appRef,
        cameraEntityRef,
        carRef,
        lightsRef,
        cameraControlsRef,
        galleryEntityRef,
        shadowCasterRef,
        
        // Methods
        initPlayCanvas,
        cleanup
    };
};

export default usePlayCanvasEngine; 